import { useEffect, useState } from 'preact/hooks';
import { currentScreen, gameSetup, activeGame } from '../../App';
import { sfx } from '../../lib/sfx';
import type { UniverseDefinition, ArchetypeDef, CharacterSheet, DurationTier, GameMode, Language } from '@mxltiverse/shared';
import { STAT_POOL, STAT_MIN, STAT_MAX, MAX_HP_BASE, DURATION_TIERS } from '@mxltiverse/shared';

type Lang = 'es' | 'en' | 'pt';
type Step = 'archetype' | 'stats' | 'background';

export function CharacterCreate() {
  const [universe, setUniverse] = useState<UniverseDefinition | null>(null);
  const [step, setStep] = useState<Step>('archetype');
  const [name, setName] = useState('');
  const [archetype, setArchetype] = useState<ArchetypeDef | null>(null);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [background, setBackground] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lang = (gameSetup.value.language ?? 'en') as Lang;

  const labels: Record<string, Record<Lang, string>> = {
    title: { es: 'CREAR PERSONAJE', en: 'CREATE CHARACTER', pt: 'CRIAR PERSONAGEM' },
    name: { es: 'Nombre del heroe', en: 'Hero name', pt: 'Nome do herói' },
    pickArchetype: { es: 'ELIGE TU ARQUETIPO', en: 'CHOOSE YOUR ARCHETYPE', pt: 'ESCOLHA SEU ARQUÉTIPO' },
    allocStats: { es: 'ASIGNA TUS STATS', en: 'ALLOCATE YOUR STATS', pt: 'DISTRIBUA SEUS ATRIBUTOS' },
    pointsLeft: { es: 'puntos restantes', en: 'points remaining', pt: 'pontos restantes' },
    writeBg: { es: 'TRASFONDO (OPCIONAL)', en: 'BACKGROUND (OPTIONAL)', pt: 'HISTÓRICO (OPCIONAL)' },
    bgPlaceholder: { es: 'Cuenta de donde viene tu personaje, sus motivaciones, su pasado...', en: 'Tell us where your character comes from, their motivations, their past...', pt: 'Conte de onde vem seu personagem, suas motivações, seu passado...' },
    back: { es: 'Volver', en: 'Back', pt: 'Voltar' },
    next: { es: 'Continuar', en: 'Continue', pt: 'Continuar' },
    confirm: { es: 'COMENZAR AVENTURA', en: 'START ADVENTURE', pt: 'COMEÇAR AVENTURA' },
    abilities: { es: 'Habilidades iniciales', en: 'Starting abilities', pt: 'Habilidades iniciais' },
    enterName: { es: 'Primero ingresa un nombre', en: 'Enter a name first', pt: 'Digite um nome primeiro' },
    loadingUniverse: { es: 'Cargando universo...', en: 'Loading universe...', pt: 'Carregando universo...' },
    creating: { es: 'Creando partida...', en: 'Creating game...', pt: 'Criando partida...' },
  };
  const tr = (k: string) => labels[k]?.[lang] ?? labels[k]?.en ?? k;

  useEffect(() => {
    const uid = gameSetup.value.universeId;
    if (!uid) { currentScreen.value = 'universe'; return; }
    fetch(`/api/universes/${uid}`)
      .then((r) => r.json())
      .then((data) => {
        setUniverse(data.universe);
        const init: Record<string, number> = {};
        data.universe.stats.forEach((s: { id: string }) => { init[s.id] = STAT_MIN; });
        setStats(init);
      })
      .catch(() => setError('Failed to load universe'));
  }, []);

  const totalAllocated = Object.values(stats).reduce((a, b) => a + b, 0);
  const remaining = STAT_POOL - totalAllocated;

  function selectArchetype(a: ArchetypeDef) {
    sfx.select();
    setArchetype(a);
    const suggested = { ...stats };
    Object.entries(a.stat_suggestions).forEach(([k, v]) => {
      suggested[k] = Math.min(v as number, STAT_MAX);
    });
    setStats(suggested);
  }

  function adjustStat(statId: string, delta: number) {
    sfx.key();
    setStats((prev) => {
      const current = prev[statId] ?? STAT_MIN;
      const next = Math.max(STAT_MIN, Math.min(STAT_MAX, current + delta));
      if (delta > 0 && remaining <= 0) return prev;
      return { ...prev, [statId]: next };
    });
  }

  function statName(id: string): string {
    return universe?.stats.find((s) => s.id === id)?.name[lang] ?? id;
  }

  function abilityName(id: string): string {
    return universe?.abilities_catalog.find((a) => a.id === id)?.name[lang] ?? id;
  }

  function nextStep() {
    sfx.click();
    if (step === 'archetype') setStep('stats');
    else if (step === 'stats') setStep('background');
  }

  function prevStep() {
    sfx.click();
    if (step === 'stats') setStep('archetype');
    else if (step === 'background') setStep('stats');
    else currentScreen.value = 'duration';
  }

  async function handleConfirm() {
    if (!archetype || !universe) return;
    sfx.success();
    setSubmitting(true);
    setError(null);

    const duration = gameSetup.value.duration as DurationTier;
    const mode: GameMode = duration === 'historia' ? 'campaign' : 'oneshot';

    const character: CharacterSheet = {
      name,
      archetype_id: archetype.id,
      stats,
      abilities: archetype.starting_abilities,
      inventory: [],
      background: background || `${archetype.name[lang]} from ${universe.name[lang]}`,
      hp: MAX_HP_BASE,
      max_hp: MAX_HP_BASE,
    };

    try {
      const res = await fetch('/api/game/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          universe_id: gameSetup.value.universeId,
          genre: gameSetup.value.genre,
          language: lang,
          mode,
          duration_tier: duration,
          character,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        sfx.error();
        setError(data.error || 'Failed to create game');
      } else {
        sfx.verify();
        activeGame.value = { saveId: data.save_id, wsUrl: data.ws_url };
        currentScreen.value = 'game';
      }
    } catch {
      sfx.error();
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  }

  if (!universe) {
    return (
      <div class="center-screen">
        <div class="pixel-border container text-center">
          <p class="text-dim">{tr('loadingUniverse')}</p>
          {error && <p class="text-red">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div class="center-screen">
      <div class="pixel-border container">
        <h2 class="pixel-title text-yellow mb-2 text-center" style={{ fontSize: '18px' }}>
          {tr('title')}
        </h2>

        {/* Step indicator */}
        <div class="mb-3 text-dim text-center" style={{ fontSize: '16px' }}>
          {step === 'archetype' && <span class="text-green">[1]</span>}
          {step !== 'archetype' && <span>[1]</span>}
          {' '}Arquetipo{'  '}
          {step === 'stats' && <span class="text-green">[2]</span>}
          {step !== 'stats' && <span>[2]</span>}
          {' '}Stats{'  '}
          {step === 'background' && <span class="text-green">[3]</span>}
          {step !== 'background' && <span>[3]</span>}
          {' '}Trasfondo
        </div>

        {/* ====== STEP 1: NAME + ARCHETYPE ====== */}
        {step === 'archetype' && (
          <>
            <input
              class="pixel-input mb-2"
              type="text"
              placeholder={tr('name')}
              value={name}
              maxLength={30}
              onFocus={() => sfx.focus()}
              onInput={(e) => { sfx.key(); setName((e.target as HTMLInputElement).value); }}
            />

            <h3 class="text-yellow mb-1" style={{ fontSize: '18px' }}>{tr('pickArchetype')}</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', maxHeight: '50vh', overflowY: 'auto' }}>
              {universe.archetypes.map((a) => (
                <div
                  key={a.id}
                  class="pixel-border"
                  style={{
                    cursor: 'pointer',
                    padding: '0.5rem',
                    borderColor: archetype?.id === a.id ? 'var(--accent-green)' : undefined,
                    background: archetype?.id === a.id ? 'var(--bg-panel-light)' : undefined,
                  }}
                  onClick={() => selectArchetype(a)}
                >
                  <h4 class="text-yellow" style={{ fontSize: '17px' }}>{a.name[lang] ?? a.name.en}</h4>
                  <p class="text-dim" style={{ fontSize: '13px' }}>{a.description[lang] ?? a.description.en}</p>
                </div>
              ))}
            </div>

            {archetype && (
              <div class="mt-2">
                <p class="text-dim" style={{ fontSize: '14px' }}>{tr('abilities')}:</p>
                {archetype.starting_abilities.map((ab) => (
                  <span class="ability-chip" key={ab}>{abilityName(ab)}</span>
                ))}
              </div>
            )}
          </>
        )}

        {/* ====== STEP 2: STATS ====== */}
        {step === 'stats' && (
          <>
            <h3 class="text-yellow mb-1" style={{ fontSize: '18px' }}>{tr('allocStats')}</h3>
            <p class={remaining === 0 ? 'text-green' : 'text-dim'} style={{ fontSize: '16px' }}>
              {remaining} {tr('pointsLeft')}
            </p>

            <div class="mt-2">
              {universe.stats.map((stat) => {
                const val = stats[stat.id] ?? STAT_MIN;
                return (
                  <div key={stat.id} class="stat-row">
                    <div>
                      <span class="text-yellow">{stat.name[lang]}</span>
                      <p class="text-dim" style={{ fontSize: '13px' }}>{stat.description[lang]}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <button
                        class="stat-btn"
                        disabled={val <= STAT_MIN}
                        onClick={() => adjustStat(stat.id, -1)}
                      >-</button>
                      <span class="text-green" style={{ fontSize: '24px', minWidth: '2ch', textAlign: 'center' }}>{val}</span>
                      <button
                        class="stat-btn"
                        disabled={val >= STAT_MAX || remaining <= 0}
                        onClick={() => adjustStat(stat.id, +1)}
                      >+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ====== STEP 3: BACKGROUND ====== */}
        {step === 'background' && (
          <>
            <h3 class="text-yellow mb-1" style={{ fontSize: '18px' }}>{tr('writeBg')}</h3>

            <div class="mb-2 pixel-border" style={{ padding: '0.5rem' }}>
              <p class="text-dim" style={{ fontSize: '14px' }}>Resumen:</p>
              <p><span class="text-yellow">{name}</span> — <span class="text-green">{archetype?.name[lang]}</span></p>
              <div style={{ marginTop: '0.3rem' }}>
                {Object.entries(stats).map(([k, v]) => (
                  <span key={k} style={{ fontSize: '14px', marginRight: '0.8rem' }}>
                    {statName(k)}: <span class={v >= 7 ? 'text-green' : v <= 3 ? 'text-red' : ''}>{v}</span>
                  </span>
                ))}
              </div>
            </div>

            <textarea
              class="pixel-textarea"
              placeholder={tr('bgPlaceholder')}
              value={background}
              onFocus={() => sfx.focus()}
              onInput={(e) => setBackground((e.target as HTMLTextAreaElement).value)}
            />
          </>
        )}

        {/* Error feedback */}
        {error && <p class="text-red" style={{ fontSize: '15px' }}>{error}</p>}

        {/* Navigation */}
        <div class="mt-3" style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button class="pixel-btn" onClick={prevStep}>{tr('back')}</button>

          {step !== 'background' && (
            <button
              class="pixel-btn text-green"
              disabled={step === 'archetype' && (!name.trim() || !archetype)}
              onClick={nextStep}
            >
              {tr('next')}
            </button>
          )}

          {step === 'background' && (
            <button
              class="pixel-btn text-green"
              disabled={submitting}
              onClick={handleConfirm}
            >
              {submitting ? tr('creating') : tr('confirm')}
            </button>
          )}
        </div>

        {/* Archetype step name validation hint */}
        {step === 'archetype' && (!name.trim() || !archetype) && (
          <p class="text-dim text-center mt-2" style={{ fontSize: '14px' }}>
            {!name.trim() ? tr('enterName') : ''}
          </p>
        )}
      </div>
    </div>
  );
}
